import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoText, DiscoTextButton } from "@disco-ui"
import { graphql, useLazyLoadQuery, useFragment } from "react-relay"
import { BrainSearchRelatedQuestionListFragment$key } from "./__generated__/BrainSearchRelatedQuestionListFragment.graphql"
import { BrainSearchRelatedQuestionListQuery } from "./__generated__/BrainSearchRelatedQuestionListQuery.graphql"

interface Props {
  brainSearchMessageId: GlobalID
  onCreateMessage: (prompt: string, sourceRelatedQuestionId?: GlobalID | null) => void
}

function BrainSearchRelatedQuestionList({
  brainSearchMessageId,
  onCreateMessage,
}: Props) {
  const classes = useStyles()

  const { node } = useLazyLoadQuery<BrainSearchRelatedQuestionListQuery>(
    graphql`
      query BrainSearchRelatedQuestionListQuery($id: ID!) {
        node(id: $id) {
          __typename
          id
          ...BrainSearchRelatedQuestionListFragment
        }
      }
    `,
    { id: brainSearchMessageId },
    { fetchPolicy: "network-only" }
  )

  const message = useFragment<BrainSearchRelatedQuestionListFragment$key>(
    graphql`
      fragment BrainSearchRelatedQuestionListFragment on BrainSearchMessage {
        id
        brainSearchRelatedQuestions(first: 3) {
          totalCount
          edges {
            node {
              id
              text
            }
          }
          totalCount
        }
      }
    `,
    node
  )

  const relatedQuestions = Relay.connectionToArray(message?.brainSearchRelatedQuestions)

  if (!message || relatedQuestions.length === 0) {
    return null
  }

  return (
    <div className={classes.wrapper}>
      <DiscoText variant={"heading-xs-700"} className={classes.titleText}>
        {"Related Questions"}
      </DiscoText>
      <div className={classes.questionsBoxContent}>
        {relatedQuestions.map((question) => (
          <DiscoTextButton
            key={question.id}
            onClick={() => onCreateMessage(question.text, question.id)}
            color={"text.secondary"}
            textVariant={"body-sm"}
            className={classes.questionItem}
          >
            {question.text}
          </DiscoTextButton>
        ))}
      </div>
    </div>
  )
}

const useStyles = makeUseStyles((theme) => ({
  wrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    alignSelf: "stretch",
    marginTop: theme.spacing(2),
    width: "100%",
  },
  titleText: {
    width: "100%",
    padding: theme.spacing(0.5, 0),
    color: theme.palette.text.primary,
  },
  questionsBoxContent: {
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(1),
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.grey[300]}`,
    boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
    borderRadius: theme.spacing(2),
    gap: theme.spacing(0.5),
  },
  questionItem: {
    width: "100%",
    justifyContent: "flex-start",
    textAlign: "left",
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(2),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "& .MuiButton-label, & .MuiButton-root, & > span": {
      textAlign: "left",
      width: "100%",
      display: "block",
    },
  },
}))

export default Relay.withSkeleton({
  component: BrainSearchRelatedQuestionList,
  skeleton: () => null,
})
